import { ChoiceItem } from '@ingka/choice';
import { ComponentIds } from '@inter-ikea-core/core';
import { OpeningFunction, ProductType } from '@inter-ikea-storageone/range';
import { reportSelectOpeningFunction } from 'analytics/reporters';
import { useCore } from 'contexts/core';
import { getMarketProducts } from 'ducks/products';
import {
  changeAllOpeningFunctionInScene,
  getFrontOpeningFunctionsInScene
} from 'rangeConfig/shared/utils/frontLayouts';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import SkapaChoice from 'ui/Choice/Choice';
import { isMobile } from 'utils/platform';
import HandIcon from './Images/HandIcon.svg';
import HandlesIcon from './Images/HandlesIcon.svg';
import { useBehaviorTracker } from 'contexts/behaviors';

const Wrapper = styled.div`
  margin-top: ${({ theme }) => (isMobile() ? 0 : theme.space.xl)};

  .aspect-ratio-box .image {
    object-fit: contain;
    padding: 12px;
  }
`;

const openingTypeChoiceOptions = [
  {
    id: 'push-open',
    title: 'storageone.productPushToOpen',
    caption: 'storageone.productListNoHandlesOptionTitle',
    openingFunction: ProductType.BESTA_PUSH_OPEN,
    image: { src: HandIcon, alt: 'storageone.productPushToOpen' }
  },
  {
    id: 'soft-closing',
    title: 'storageone.productSoftClosing',
    caption: 'storageone.productListSoftClosingWithHandlesSubtitle',
    openingFunction: ProductType.BESTA_SOFT_CLOSING,
    image: { src: HandlesIcon, alt: 'storageone.productSoftClosing' }
  }
];

const openingFunctionMap = {
  [ProductType.BESTA_PUSH_OPEN as string]: 'push_to_open',
  [ProductType.BESTA_SOFT_CLOSING as string]: 'soft_closing'
};

const ChoiceChildren = () => {
  const coreApi = useCore();
  const { formatMessage } = useIntl();
  const behaviorTracker = useBehaviorTracker();
  const products = useSelector(getMarketProducts);

  const onToggleSelect = (openingFunction: OpeningFunction) =>
    behaviorTracker.custom(() => {
      changeAllOpeningFunctionInScene(openingFunction, products, coreApi);
      if (openingFunction === ProductType.BESTA_PUSH_OPEN) {
        const handles = coreApi.entities.getByComponent(ComponentIds.HANDLE_COMPONENT_ID);
        coreApi.entities.deleteMultiple(handles);
      }

      reportSelectOpeningFunction({ tab: openingFunctionMap[openingFunction] });
    }, true);

  return openingTypeChoiceOptions.map(option => (
    <ChoiceItem
      id={option.id}
      title={formatMessage({ id: option.title })}
      caption={option.caption && formatMessage({ id: option.caption })}
      image={{ src: option.image?.src, alt: formatMessage({ id: option.title }) }}
      imagePosition="leading"
      onClick={() => onToggleSelect(option.openingFunction as OpeningFunction)}
      key={option.id}
    />
  ));
};

export default function FrontOpeningFunctionToggleMainMenu() {
  const coreApi = useCore();
  const currentOpeningFunctions = getFrontOpeningFunctionsInScene(coreApi);
  const pushOpen = currentOpeningFunctions.every(
    openingFunction => openingFunction === ProductType.BESTA_PUSH_OPEN
  );
  const softClosing = currentOpeningFunctions.every(
    openingFunction => openingFunction === ProductType.BESTA_SOFT_CLOSING
  );
  const selectedIndex = pushOpen
    ? openingTypeChoiceOptions.findIndex(
        option => option.openingFunction === ProductType.BESTA_PUSH_OPEN
      )
    : softClosing
    ? openingTypeChoiceOptions.findIndex(
        option => option.openingFunction === ProductType.BESTA_SOFT_CLOSING
      )
    : undefined;

  return (
    <Wrapper>
      <SkapaChoice children={ChoiceChildren()} preselectedIndex={selectedIndex} />
    </Wrapper>
  );
}
